import { firePurchaseConversion } from "@/lib/google-ads";
import { sleep } from "@/utils/sleep";
import {
  getCertificateCheckout,
  type CertificateCheckoutStatusResponse,
} from "@/services/certificatePaymentsService";

/**
 * Where a consumer goes once their certificate checkout is confirmed paid.
 *
 * EXTRACTED SO BOTH PAYMENT PATHS SHARE IT. dLocal returns the consumer to
 * /pay/dlocal-return and Mercado Pago never leaves /pay/mercadopago, but what
 * has to happen next is identical and NOT obvious: the quote branch below is
 * the kind of thing that gets re-derived wrongly the second time someone
 * writes it, dropping an RFQ payer into a blank new-request wizard.
 */

/**
 * Poll briefly for the request spawned by a paid RFQ checkout.
 *
 * Deliberately bounded and non-fatal: the spawn is fire-and-forget off the
 * payment event, and a scheduled sweep recovers it if it never ran.
 *
 * IT NO LONGER PICKS THE DESTINATION — see navigateAfterCertificatePaid. It
 * exists so the children have a moment to appear before the RFQ renders, and
 * the payer arrives at leg rows they can walk into rather than at a card that
 * will fill in underneath them.
 */
const SPAWN_POLL_ATTEMPTS = 5;
const SPAWN_POLL_MS = 1500;

async function waitForSpawnedChild(checkoutId: string, known: string | null): Promise<string | null> {
  if (known) return known;
  for (let i = 0; i < SPAWN_POLL_ATTEMPTS; i++) {
    await sleep(SPAWN_POLL_MS);
    try {
      const row = await getCertificateCheckout(checkoutId);
      if (row.childRequestId) return row.childRequestId;
    } catch {
      // KEEP GOING. One flaky request must not discard the remaining attempts:
      // the whole point of the loop is to outlast a moment of noise, and
      // giving up on the first error sends the payer to the RFQ page for a
      // child request that was about to appear.
      continue;
    }
  }
  return null;
}

/**
 * Fires the purchase conversion and navigates. Never returns to the caller in
 * practice — it replaces the document location.
 */
export async function navigateAfterCertificatePaid(
  checkoutId: string,
  row: CertificateCheckoutStatusResponse,
): Promise<void> {
  firePurchaseConversion({
    transactionId: checkoutId,
    value: row.amountMinor / 100,
    currency: row.currency,
  });

  // An RFQ checkout is paid from an existing request's detail page, not from
  // the wizard, so the wizard resume below is the wrong destination for it —
  // it would drop the payer into a blank new request with no trace of what
  // they just paid for.
  if (row.pricingSource === "quote" && row.quoteRequestId) {
    /*
      ALWAYS THE RFQ, never one of its children.

      This used to prefer the spawned child, which is right when there is only
      one — but a two-discipline RFQ is paid by ONE checkout covering BOTH legs
      (the payment panel will not let you pay until every discipline is
      settled), so TWO children spawn and `childRequestId` is a single field.
      The payer was dropped on whichever one it happened to hold and the other
      was nowhere on screen. Reported from a real two-leg purchase: paid both,
      landed on the contador's trámite alone.

      The row cannot tell the two cases apart — it carries no leg count — and
      the RFQ is the designed hub for this exact moment: once every leg has a
      child, quote-request-legs-card switches to its "started" wording and each
      leg row becomes the way into its trámite.

      ACCEPTED COST: a single-discipline payer now takes one extra click to
      reach their work. That is the trade for never hiding half of a
      two-discipline purchase. Counting legs to branch would buy the click back
      for another request and another failure mode, on a payment path.
    */
    await waitForSpawnedChild(checkoutId, row.childRequestId ?? null);
    window.location.replace(`/requests/${row.quoteRequestId}?source=quote`);
    return;
  }

  window.location.replace(
    `/requests/new?certificate_payment=done&checkout_id=${encodeURIComponent(checkoutId)}`,
  );
}
